// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useCallback, useState } from "react";
import { pagesApi } from "../api/pagesApi";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("sb_token") || "");
  const [user, setUser] = useState(() => safeJsonParse(localStorage.getItem("sb_user")));
  const [loading, setLoading] = useState(true);

  const clearStorage = useCallback(() => {
    localStorage.removeItem("sb_token");
    localStorage.removeItem("sb_user");
  }, []);

  const logout = useCallback(() => {
    clearStorage();
    setToken("");
    setUser(null);
    setLoading(false);
  }, [clearStorage]);

  const login = useCallback((newToken, newUser) => {
    localStorage.setItem("sb_token", newToken);
    localStorage.setItem("sb_user", JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
    setLoading(false);
  }, []);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("sb_token") || "";
    if (!t) {
      setToken("");
      setUser(null);
      setLoading(false);
      return;
    }

    setToken(t);

    try {
      // Validate token and get fresh user from server
      const me = await pagesApi.me();
      if (me?.user) {
        setUser(me.user);
        localStorage.setItem("sb_user", JSON.stringify(me.user));
      } else {
        logout();
        return;
      }
    } catch {
      // Token invalid/expired
      logout();
      return;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => {
    return {
      token,
      user,
      loading,
      isAuthenticated: !!token && !!user,
      login,
      logout,
      refresh,
    };
  }, [token, user, loading, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}